import { FaArrowLeft, FaBolt, FaShieldAlt, FaChartLine, FaCheckCircle, FaLightbulb } from "react-icons/fa";
import BackendScreenerTable, { ColumnConfig } from "../screener/BackendScreenerTable.tsx";
import PremiumAccessPage from "../PremiumAccessPage.tsx";
import ViewModeToggle from "../common/ViewModeToggle.tsx";
import ChartGridView from "../common/ChartGridView.tsx";
import ScreenerFilterBar from "../common/ScreenerFilterBar.tsx";

import { useNavigate } from "react-router-dom";
import React, { useState } from "react";
import '../SwingScreener.css';
import './PositiveGapUpGuide.css';
import { usePremium } from "../../hooks/usePremium.ts";
import useScreenerData from "../../hooks/useScreenerData.ts";
import useScreenerHistory from "../../hooks/useScreenerHistory.ts";
import { ScreenerStockFilters } from "../../services/screenerApi.ts";
import { SCREENER_PAGE_SIZE, SCREENER_HISTORY_PAGE_SIZE } from "../../constants/screener.ts";
import PostCaptureInfo from "../common/PostCaptureInfo.tsx";


const MomentumScanner = () => {

    const navigate = useNavigate();
    const isPremium = usePremium();
    const [activeTab, setActiveTab] = useState<'stocks' | 'insights' | 'howto'>(!isPremium ? 'insights' : 'stocks');
    const [viewMode, setViewMode] = useState<'list' | 'chart'>('list');
    const [insightsViewMode, setInsightsViewMode] = useState<'list' | 'chart'>('list');
    const [filters, setFilters] = useState<ScreenerStockFilters>({});

    // Shared data for both list and chart views — fetched once via hook
    const { data: stocksData, loading: stocksLoading, error: stocksError,
            currentPage, totalPages, totalItems, setCurrentPage } = useScreenerData('MOMENTUM_AI', isPremium, filters);
    const { data: historyData, loading: historyLoading, error: historyError,
            currentPage: historyPage, totalPages: historyTotalPages, totalItems: historyTotalItems,
            setCurrentPage: setHistoryPage } = useScreenerHistory('MOMENTUM_AI');

    // Columns for stocks tab
    const stocksColumns: ColumnConfig[] = [
        { key: 'stockName', label: 'Stock Name', visible: true },
        { key: 'symbol', label: 'Symbol', visible: true },
        { key: 'capturedPrice', label: 'Captured Price', visible: true },
        { key: 'capturedOn', label: 'Captured On', visible: true },
    ];

    // Columns for backtest/history tab with maxUpside
    const historyColumns: ColumnConfig[] = [
        { key: 'stockName', label: 'Stock Name', visible: true },
        { key: 'symbol', label: 'Symbol', visible: true },
        { key: 'capturedPrice', label: 'Captured Price', visible: true },
        { key: 'currentPrice', label: 'Current Price', visible: true },
        { key: 'maxUpside', label: 'Max Upside %', visible: true },
        { key: 'capturedOn', label: 'Captured On', visible: true },
    ];

    return (
        <>
            <div className="main-content">
                {/* Header */}
                <div className="chart-header">
                    <button className="back-btn" onClick={() => navigate(-1)}>
                        <FaArrowLeft />
                    </button>
                    <h2 className="chart-title">Swing Momentum Scanner 🚀</h2>
                </div>

                {/* Accuracy banner — hidden temporarily */}
                {/* <div className="accuracy-banner">
                    <span className="accuracy-dot" />
                    <div className="accuracy-badge">
                        <span className="accuracy-badge-icon">🎯</span>
                        ~72% Accuracy
                    </div>
                    <div className="accuracy-banner-text">
                        <span className="accuracy-banner-title">AI-Verified Pattern Accuracy</span>
                        <span className="accuracy-banner-sub">Based on historical price patterns observed over the last 3 months.</span>
                    </div>
                </div> */}

                {/* Tabs */}
                <div className="scanner-tabs">
                    <button
                        className={`scanner-tab ${activeTab === 'stocks' ? 'active' : ''}`}
                        onClick={() => setActiveTab('stocks')}
                    >
                        Stocks
                    </button>
                    {/* Post Capture Insights tab — hidden temporarily */}
                    {/* <button
                        className={`scanner-tab ${activeTab === 'insights' ? 'active' : ''}`}
                        onClick={() => setActiveTab('insights')}
                    >
                        Post Capture Insights (Backtest)
                    </button> */}
                    <button
                        className={`scanner-tab ${activeTab === 'howto' ? 'active' : ''}`}
                        onClick={() => setActiveTab('howto')}
                    >
                        How to? 💡 ▶️
                    </button>
                </div>

                {/* View Mode Toggle + Filters - stocks tab */}
                {activeTab === 'stocks' && isPremium && (
                    <div className="screener-toolbar">
                        <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
                        <div className="screener-toolbar-filter">
                            <ScreenerFilterBar onApply={setFilters} />
                        </div>
                    </div>
                )}

                {/* View Mode Toggle - insights/backtest tab */}
                {activeTab === 'insights' && (
                    <ViewModeToggle viewMode={insightsViewMode} onViewModeChange={setInsightsViewMode} />
                )}

                {/* Tab Content */}
                {activeTab === 'stocks' && (
                    !isPremium ? (<PremiumAccessPage />) : (
                        viewMode === 'list' ? (
                            <BackendScreenerTable
                                screenerCode="MOMENTUM_AI"
                                pageSize={SCREENER_PAGE_SIZE}
                                columns={stocksColumns}
                                viewChartButtonEnable={false}
                                data={stocksData}
                                loading={stocksLoading}
                                error={stocksError}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalItems={totalItems}
                                onPageChange={setCurrentPage}
                            />
                        ) : (
                            <ChartGridView
                                isActive={activeTab === 'stocks' && viewMode === 'chart'}
                                data={stocksData}
                                loading={stocksLoading}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalItems={totalItems}
                                pageSize={SCREENER_PAGE_SIZE}
                                onPageChange={setCurrentPage}

                                screenerCode="MOMENTUM_AI"
                                smaValues={[21, 50]}
                            />
                        )
                    )
                )}

                {activeTab === 'insights' && (
                    <>
                    <PostCaptureInfo onViewLive={() => setActiveTab('stocks')} />
                    {insightsViewMode === 'list' ? (
                        <BackendScreenerTable
                            screenerCode="MOMENTUM_AI"
                            pageSize={SCREENER_HISTORY_PAGE_SIZE}
                            columns={historyColumns}
                            viewChartButtonEnable={false}
                            data={historyData}
                            loading={historyLoading}
                            error={historyError}
                            currentPage={historyPage}
                            totalPages={historyTotalPages}
                            totalItems={historyTotalItems}
                            onPageChange={setHistoryPage}
                        />
                    ) : (
                        <ChartGridView
                            isActive={activeTab === 'insights' && insightsViewMode === 'chart'}
                            data={historyData}
                            loading={historyLoading}
                            currentPage={historyPage}
                            totalPages={historyTotalPages}
                            totalItems={historyTotalItems}
                            pageSize={SCREENER_HISTORY_PAGE_SIZE}
                            onPageChange={setHistoryPage}
                            isAIScreener={true}
                            screenerCode="MOMENTUM_AI"
                            smaValues={[21, 50]}
                        />
                    )}
                    </>
                )}

                {/* How to Tab */}
                {activeTab === 'howto' && (
                    <div className="howto-container">

                        {/* Hero */}
                        <div className="howto-hero">
                            <div className="howto-hero-icon">🚀</div>
                            <h1 className="howto-hero-title">Momentum AI Strategy</h1>
                            <p className="howto-hero-subtitle">
                                An AI-powered momentum screener designed to surface stocks exhibiting strong, sustained price momentum — backed by volume confirmation, trend strength, and multi-layer signal validation.
                            </p>
                        </div>

                        {/* Strategy Video — hidden until video is ready */}
                        {/* <div className="howto-video-section">
                            <div className="howto-video-label">📹 Strategy Walkthrough</div>
                            <div className="howto-video-wrap">
                                <video
                                    className="howto-video-player"
                                    src="https://lorem.video/720p"
                                    poster="https://img.magnific.com/free-psd/creative-youtube-thumbnail-design-template_505751-6437.jpg"
                                    controls
                                    controlsList="nodownload"
                                    playsInline
                                    preload="metadata"
                                />
                            </div>
                        </div> */}

                        {/* What is it */}
                        <div className="howto-section">
                            <div className="howto-section-header">
                                <FaLightbulb className="howto-section-icon" />
                                <h2>What is Momentum AI?</h2>
                            </div>
                            <p className="howto-text">
                                Momentum AI goes beyond simple price-based filters. It uses a proprietary AI engine to detect stocks where multiple momentum signals are aligning simultaneously — price strength, volume expansion, trend continuity, and relative outperformance. The result is a curated list of high-probability momentum setups that would be nearly impossible to track manually.
                            </p>

                            <div className="whatisit-grid">
                                <div className="whatisit-card">
                                    <span className="whatisit-icon">🤖</span>
                                    <div>
                                        <strong>AI-Powered Signal Detection</strong>
                                        <p>The engine processes hundreds of data points per stock — price structure, volume patterns, trend strength, and historical momentum behaviour — to assign a signal score. Only stocks crossing the AI threshold are shortlisted, filtering out weak or noise-driven moves.</p>
                                    </div>
                                </div>
                                <div className="whatisit-card">
                                    <span className="whatisit-icon">📊</span>
                                    <div>
                                        <strong>Volume-Confirmed Momentum</strong>
                                        <p>Price momentum without volume is unreliable. The AI validates each signal with above-average volume expansion, ensuring that the move is backed by genuine participation rather than thin, manipulated price action.</p>
                                    </div>
                                </div>
                                <div className="whatisit-card">
                                    <span className="whatisit-icon">📈</span>
                                    <div>
                                        <strong>Trend Continuity Filter</strong>
                                        <p>Momentum is most powerful when it aligns with an existing trend. The screener checks whether the stock is in a well-defined uptrend at multiple timeframes before flagging it — avoiding counter-trend traps that catch many traders off guard.</p>
                                    </div>
                                </div>
                                <div className="whatisit-card">
                                    <span className="whatisit-icon">💪</span>
                                    <div>
                                        <strong>Relative Strength Analysis</strong>
                                        <p>Stocks that outperform their sector and the broader index during consolidation phases tend to lead the next leg up. The AI measures relative strength against both the sector and Nifty to identify true leaders rather than random movers.</p>
                                    </div>
                                </div>
                                <div className="whatisit-card">
                                    <span className="whatisit-icon">🏦</span>
                                    <div>
                                        <strong>Institutional Footprint Detection</strong>
                                        <p>Sustained momentum is rarely retail-driven. The AI cross-references price and volume patterns against known institutional accumulation signatures — identifying setups where smart money appears to be building positions systematically.</p>
                                    </div>
                                </div>
                                <div className="whatisit-card">
                                    <span className="whatisit-icon">📋</span>
                                    <div>
                                        <strong>Quarterly Results Validation</strong>
                                        <p>Strong price momentum aligned with improving fundamentals is far more reliable than price action alone. The AI checks recent quarterly results — revenue growth, profit expansion, margin trends, and earnings beats — to confirm that the momentum has a fundamental backing and is not purely sentiment-driven.</p>
                                    </div>
                                </div>
                                <div className="whatisit-card">
                                    <span className="whatisit-icon">👥</span>
                                    <div>
                                        <strong>Promoter & Shareholder Activity</strong>
                                        <p>Promoter holding patterns and changes in FII/DII/MF shareholding are powerful early signals. Rising promoter confidence (increased stake or no pledging) combined with growing institutional participation often precedes major price moves. The AI monitors these patterns to separate high-conviction setups from ordinary price moves.</p>
                                    </div>
                                </div>
<div className="whatisit-card">
                                    <span className="whatisit-icon">🔬</span>
                                    <div>
                                        <strong>Multi-Layer Momentum Scoring</strong>
                                        <p>Each stock passes through a layered scoring model — short-term momentum, medium-term trend, volume consistency, price structure quality, fundamental validation, and shareholder activity — before being included. What you see is the output of a rigorous, multi-dimensional analysis.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="whatisit-summary">
                                <span>💡</span>
                                <p>In short — Momentum AI does in seconds what would take hours of manual screening. It identifies stocks where price, volume, trend, and institutional signals are simultaneously aligned — giving you a structured starting point for further research.</p>
                            </div>
                        </div>

                        {/* Rules Grid */}
                        <div className="howto-rules-grid">

                            {/* Entry */}
                            <div className="howto-rule-card entry">
                                <div className="howto-rule-header">
                                    <FaBolt className="rule-icon" />
                                    <h3>Strategy Entry Level</h3>
                                </div>
                                <div className="howto-rule-body">
                                    <div className="rule-item">
                                        <span className="rule-label">Entry Trigger</span>
                                        <span className="rule-value">High of the captured candle</span>
                                    </div>
                                    <div className="rule-item">
                                        <span className="rule-label">Buffer Rule</span>
                                        <span className="rule-value">~0.025% above candle high</span>
                                    </div>
                                    <div className="rule-example">
                                        <span className="example-label">Illustrative Example</span>
                                        <div className="example-box">
                                            <div className="example-row">
                                                <span>Captured Candle High</span>
                                                <strong>₹100.00</strong>
                                            </div>
                                            <div className="example-row highlight">
                                                <span>Entry trigger (with buffer)</span>
                                                <strong>₹100.03</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="rule-note">
                                        The strategy uses the high of the captured candle as the entry trigger. A small buffer (~0.025%) is added above the high — especially when the closing price is very close to the candle high — to account for price precision and avoid being triggered at the exact extreme. This does not constitute a buy recommendation or financial advice.
                                    </p>
                                </div>
                            </div>

                            {/* Risk */}
                            <div className="howto-rule-card stoploss">
                                <div className="howto-rule-header">
                                    <FaShieldAlt className="rule-icon" />
                                    <h3>Strategy Risk Level</h3>
                                </div>
                                <div className="howto-rule-body">
                                    <div className="rule-item">
                                        <span className="rule-label">Risk Level</span>
                                        <span className="rule-value">Low of the captured candle</span>
                                    </div>
                                    <div className="rule-item">
                                        <span className="rule-label">Strategy Design</span>
                                        <span className="rule-value">Built for tight candle structures (&lt;5% range)</span>
                                    </div>
                                    <div className="rule-item">
                                        <span className="rule-label">Minimum Gap Rule</span>
                                        <span className="rule-value">At least 3% from entry</span>
                                    </div>
                                    <div className="rule-example">
                                        <span className="example-label">Illustrative Example</span>
                                        <div className="example-box">
                                            <div className="example-row">
                                                <span>Entry trigger</span>
                                                <strong>₹100.03</strong>
                                            </div>
                                            <div className="example-row danger">
                                                <span>Risk level (candle low)</span>
                                                <strong>₹96.00 (~4% below)</strong>
                                            </div>
                                            <div className="example-row">
                                                <span>If candle low is ₹98 (&lt;3% gap)</span>
                                                <strong>Adjusted to ₹97.03 (~3%)</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="rule-note">
                                        The strategy marks the low of the captured candle as the risk level, with a small buffer below when the closing price is very near the low. Momentum AI is specifically designed to capture stocks with tight candle structures — the screener itself filters for setups where the high-to-low range is naturally below 5%. If the gap between the candle high and low is less than 3%, the risk level is adjusted downward to maintain a minimum 3% gap — this reduces the likelihood of being knocked out by normal price noise. This does not constitute financial advice.
                                    </p>
                                </div>
                            </div>

                            {/* Exit */}
                            <div className="howto-rule-card target">
                                <div className="howto-rule-header">
                                    <FaChartLine className="rule-icon" />
                                    <h3>Strategy Exit Approach</h3>
                                </div>
                                <div className="howto-rule-body">
                                    <div className="rule-item success">
                                        <span className="rule-label">Minimum Expected Move</span>
                                        <span className="rule-value">10–12% from entry</span>
                                    </div>
                                    <div className="rule-item">
                                        <span className="rule-label">Beginner Approach</span>
                                        <span className="rule-value">Full exit at 10–12%</span>
                                    </div>
                                    <div className="rule-item">
                                        <span className="rule-label">Advanced Approach</span>
                                        <span className="rule-value">Exit 50% at 10–12%, trail the rest</span>
                                    </div>
                                    <div className="success-box">
                                        <FaCheckCircle />
                                        <p>The strategy targets a minimum move of 10–12% from the entry level. For those starting out, exiting the full position at this level keeps the approach simple and disciplined. For those comfortable with trailing, exiting half at 10–12% locks in a base gain while the remaining position is trailed to capture any extended momentum. This is how the strategy defines exits — it is not financial advice.</p>
                                    </div>
                                    <div className="rule-example">
                                        <span className="example-label">Illustrative Example</span>
                                        <div className="example-box">
                                            <div className="example-row">
                                                <span>Entry (100 qty)</span>
                                                <strong>₹100.03</strong>
                                            </div>
                                            <div className="example-row success">
                                                <span>Beginner — full exit at ~10%</span>
                                                <strong>₹110.03</strong>
                                            </div>
                                            <div className="example-row success">
                                                <span>Advanced — exit 50 qty at ~10%</span>
                                                <strong>₹110.03</strong>
                                            </div>
                                            <div className="example-row highlight">
                                                <span>Advanced — remaining 50 qty</span>
                                                <strong>Trailing 📈</strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* Summary Cheatsheet */}
                        <div className="howto-cheatsheet">
                            <h3>📋 Strategy Summary</h3>
                            <div className="cheatsheet-grid">
                                <div className="cheatsheet-item">
                                    <span className="cheat-icon">⚡</span>
                                    <div>
                                        <strong>Entry Level</strong>
                                        <span>Candle high + ~0.025% buffer</span>
                                    </div>
                                </div>
                                <div className="cheatsheet-item">
                                    <span className="cheat-icon">🛡️</span>
                                    <div>
                                        <strong>Risk Level</strong>
                                        <span>Candle low · min 3% gap · tight by design</span>
                                    </div>
                                </div>
                                <div className="cheatsheet-item">
                                    <span className="cheat-icon">📈</span>
                                    <div>
                                        <strong>Beginner Exit</strong>
                                        <span>Full exit at 10–12% move</span>
                                    </div>
                                </div>
                                <div className="cheatsheet-item">
                                    <span className="cheat-icon">💰</span>
                                    <div>
                                        <strong>Advanced Exit</strong>
                                        <span>50% at 10–12%, trail the rest</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* SEBI Disclaimer */}
                        <div className="sebi-disclaimer">
                            <span className="sebi-icon">⚠️</span>
                            <div>
                                <p>SwingSetupAI is not a SEBI registered investment advisor, research analyst, or portfolio manager. All content, screeners, strategies, and information provided on this platform are strictly for educational and informational purposes only and do not constitute investment advice, buy/sell recommendations, or any form of financial guidance. Trading in equities, derivatives, and other financial instruments involves substantial risk of loss. Past performance of any strategy or screener does not guarantee future results. Users are solely responsible for their own investment decisions. Please consult a SEBI registered investment advisor before making any financial decisions.</p>
                            </div>
                        </div>

                    </div>
                )}
            </div>
        </>
    );
}

export default MomentumScanner;
